import { use, useMemo } from "react";
import { type GQL } from "@binale-tech/shared";
import { type TCashRegisterTransactionData } from "@cash-register/types";
import { CashRegisterContext, CashRegisterTransactionsContext } from "@domain/context/CashRegisterContext";
import { type MetricStatus } from "./useGuVMetrics";

export interface CashRegisterBalance {
    id: string;
    name: string;
    /** cents */
    balance: number;
}

export interface CashMetrics {
    status: MetricStatus;
    /** cents, EUR registers only */
    total: number;
    registers: CashRegisterBalance[];
}

export const computeCashBalances = (
    registers: GQL.ICashRegister[],
    transactionsData: Record<string, TCashRegisterTransactionData>,
): CashRegisterBalance[] =>
    registers
        .filter((register) => String(register.currency || "EUR") === "EUR")
        .map((register) => {
            let balance = register.openingBalance ?? 0;
            Object.values(transactionsData[register.id] || {}).forEach((tx) => {
                if (String(tx.currency || "EUR") !== "EUR") {
                    return;
                }
                balance += tx.amount ?? 0;
            });
            return { id: register.id, name: register.name, balance };
        });

export const useCashMetrics = (): CashMetrics => {
    const { registers } = use(CashRegisterContext);
    const { transactionsData } = use(CashRegisterTransactionsContext);
    return useMemo(() => {
        // no isLoaded flag on the registers listener: an empty list shows as "empty" (block hidden)
        if (!registers.length) {
            return { status: "empty" as const, total: 0, registers: [] as CashRegisterBalance[] };
        }
        const balances = computeCashBalances(registers, transactionsData);
        return {
            status: "ready" as const,
            total: balances.reduce((acc, r) => acc + r.balance, 0),
            registers: balances,
        };
    }, [registers, transactionsData]);
};
